import React, {useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import ReactSelect from 'react-select'
import styled from 'styled-components'
import {normalizeSites} from '../../../config/utils'
import {Text} from '../../../sharedComponents/components'
import {colors} from '../../../sharedComponents/colors'
import {EventPayload} from '../../../state/rest'
import {useAppState} from '../../../state'

interface AromiDishSelectorProps {
  task: {
    scheduleInstance?: string
    taskGroup?: {
      name?: string
    }
  }
  payload: EventPayload
  setValue: (key: keyof EventPayload, value: any) => void
  disabled?: boolean
}

type SelectOption = {
  label: string
  value: string
  dishName: string
  mealGroup?: string
}

const buildOptionLabel = (dishName: string, mealGroup?: string): string => {
  const normalizedDishName = dishName.trim()
  const normalizedMealGroup = mealGroup?.trim()

  return normalizedMealGroup ? `${normalizedDishName} - ${normalizedMealGroup}` : normalizedDishName
}

const compareOptions = (left: SelectOption, right: SelectOption, language: string): number => {
  const dishComparison = left.dishName.localeCompare(right.dishName, language)
  if (dishComparison !== 0) {
    return dishComparison
  }

  return (left.mealGroup || '').localeCompare(right.mealGroup || '', language)
}

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`

const Label = styled.label`
  font-size: 0.875rem;
`

const HelperText = styled(Text)`
  margin-top: 0.125rem;
`

const isPreparationTask = (taskGroupName?: string): boolean =>
  (taskGroupName || '').trim().toLowerCase() === 'preparation'

const toIsoDate = (value?: string): string => {
  if (!value) {
    return new Date().toISOString().slice(0, 10)
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toISOString().slice(0, 10)
  }

  return parsed.toISOString().slice(0, 10)
}

const getLanguage = (locale?: string): string => {
  const [language] = (locale || '').split('-')
  return (language || 'FI').toUpperCase()
}

const AromiDishSelector = ({task, payload, setValue, disabled}: AromiDishSelectorProps) => {
  const {t} = useTranslation(['tasks', 'common'])
  const {state, actions} = useAppState()
  const [options, setOptions] = useState<SelectOption[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const selectedChain = state.selectedChainId ? (state.chainsById as any)?.[state.selectedChainId] : undefined
  const site =
    state.site?.id && selectedChain
      ? normalizeSites(selectedChain).find((candidate: any) => candidate.id === state.site?.id) || state.site
      : state.site
  const shouldShow = Boolean(site?.aromiIntegration && site?.id && isPreparationTask(task.taskGroup?.name))
  const productionDate = toIsoDate(payload.recordingDay || task.scheduleInstance)
  const language = getLanguage(site?.locale)

  useEffect(() => {
    let cancelled = false

    const fetchOptions = async () => {
      if (!shouldShow || !site?.id) {
        if (!cancelled) {
          setOptions([])
          setLoadError(null)
        }
        return
      }

      setLoading(true)
      setLoadError(null)

      try {
        const response = await actions.getAromiProductionItemsForSite({
          siteId: site.id,
          productionDateStart: productionDate,
          productionDateEnd: productionDate,
          language
        })

        if (cancelled) {
          return
        }

        const optionMap = new Map<string, SelectOption>()

        for (const item of response.items || []) {
          const dishName = item.aromiProduct?.trim()
          if (!dishName) {
            continue
          }

          const optionValue = buildOptionLabel(dishName, item.mealGroup)
          if (!optionMap.has(optionValue)) {
            optionMap.set(optionValue, {
              label: optionValue,
              value: optionValue,
              dishName,
              mealGroup: item.mealGroup?.trim()
            })
          }
        }

        const nextOptions = Array.from(optionMap.values()).sort((left, right) => compareOptions(left, right, language))

        setOptions(nextOptions)
      } catch (error) {
        if (cancelled) {
          return
        }

        setOptions([])
        setLoadError((error as Error)?.message || t('common:errors.general', 'Something went wrong'))
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchOptions()

    return () => {
      cancelled = true
    }
  }, [actions, language, productionDate, shouldShow, site?.id, t])

  const selectedOption = useMemo(() => {
    if (!payload.aromiProduct) {
      return null
    }

    return (
      options.find(option => option.value === payload.aromiProduct) || {
        label: payload.aromiProduct,
        value: payload.aromiProduct
      }
    )
  }, [options, payload.aromiProduct])

  if (!shouldShow) {
    return null
  }

  return (
    <FieldContainer>
      <Label htmlFor="aromi-dish-select">{t('tasks:labels.aromiDish', 'Aromi dish')}</Label>
      <ReactSelect
        inputId="aromi-dish-select"
        options={options}
        value={selectedOption}
        onChange={selection => setValue('aromiProduct', selection ? (selection as SelectOption).value : undefined)}
        placeholder={
          loading
            ? t('tasks:labels.loadingAromiDish', 'Loading Aromi dishes...')
            : t('tasks:labels.selectAromiDish', 'Search and select an Aromi dish')
        }
        isClearable
        isSearchable
        isDisabled={disabled || loading}
        noOptionsMessage={({inputValue}) =>
          inputValue
            ? t('tasks:labels.noMatchingAromiDish', 'No matching Aromi dish found')
            : t('tasks:labels.noAromiDish', 'No Aromi dishes available for this date')
        }
      />
    </FieldContainer>
  )
}

export default AromiDishSelector
